import React, { useState } from 'react';
import { Viewer, ThemeContext, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { highlightPlugin } from '@react-pdf-viewer/highlight';
import { themePlugin } from '@react-pdf-viewer/theme';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { getFilePlugin } from '@react-pdf-viewer/get-file';
import { Button, Space, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FileLoadingState, FileErrorState } from './FileViewer';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/highlight/lib/styles/index.css';

interface PDFViewerProps {
  fileUrl: string;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ fileUrl }) => {
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light');

  // Plugin instances
  const highlightPluginInstance = highlightPlugin();
  const themePluginInstance = themePlugin();
  const getFilePluginInstance = getFilePlugin();

  // Use default layout plugin which handles toolbar positioning properly
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [
      // Keep only the thumbnail and bookmark tabs
      defaultTabs[0], // Thumbnails
      defaultTabs[1], // Bookmarks
    ],
    toolbarPlugin: {
      // Customize toolbar to fix positioning issues
      renderDefaultToolbar: (Toolbar) => (
        <Toolbar>
          {(toolbarSlot) => {
            const {
              CurrentPageInput,
              Download,
              EnterFullScreen,
              GoToNextPage,
              GoToPreviousPage,
              NumberOfPages,
              Print,
              ShowSearchPopover,
              Zoom,
              ZoomIn,
              ZoomOut,
            } = toolbarSlot;

            return (
              <Space direction='horizontal'>
                <div style={{ alignItems: 'center', display: 'flex' }}>
                  <div style={{ marginRight: '0.5rem' }}>
                    <ShowSearchPopover>
                      {(props) => (
                        <Tooltip title="Search">
                          <Button
                            size="small"
                            onClick={props.onClick}
                            icon={<FontAwesomeIcon icon="fa-regular fa-magnifying-glass" />}
                          />
                        </Tooltip>
                      )}
                    </ShowSearchPopover>
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <GoToPreviousPage>
                      {(props) => (
                        <Tooltip title="Previous page">
                          <Button
                            size="small"
                            onClick={props.onClick}
                            disabled={props.isDisabled}
                            icon={<FontAwesomeIcon icon="fa-solid fa-chevron-left" />}
                          />
                        </Tooltip>
                      )}
                    </GoToPreviousPage>
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <CurrentPageInput />
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <span>/ <NumberOfPages /></span>
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <GoToNextPage>
                      {(props) => (
                        <Tooltip title="Next page">
                          <Button
                            size="small"
                            onClick={props.onClick}
                            disabled={props.isDisabled}
                            icon={<FontAwesomeIcon icon="fa-solid fa-chevron-right" />}
                          />
                        </Tooltip>
                      )}
                    </GoToNextPage>
                  </div>
                </div>

                <div style={{ flexGrow: 1 }} />

                <div style={{ alignItems: 'center', display: 'flex' }}>
                  <div style={{ marginRight: '0.5rem' }}>
                    <ZoomOut>
                      {(props) => (
                        <Tooltip title="Zoom out">
                          <Button
                            size="small"
                            onClick={props.onClick}
                            disabled={props.isDisabled}
                            icon={<FontAwesomeIcon icon="fa-solid fa-minus" />}
                          />
                        </Tooltip>
                      )}
                    </ZoomOut>
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <Zoom />
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <ZoomIn>
                      {(props) => (
                        <Tooltip title="Zoom in">
                          <Button
                            size="small"
                            onClick={props.onClick}
                            disabled={props.isDisabled}
                            icon={<FontAwesomeIcon icon="fa-solid fa-plus" />}
                          />
                        </Tooltip>
                      )}
                    </ZoomIn>
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <EnterFullScreen>
                      {(props) => (
                        <Tooltip title="Full screen">
                          <Button
                            size="small"
                            onClick={props.onClick}
                            icon={<FontAwesomeIcon icon="fa-solid fa-expand" />}
                          />
                        </Tooltip>
                      )}
                    </EnterFullScreen>
                  </div>
                  <div style={{ marginRight: '0.5rem' }}>
                    <Download>
                      {(props) => (
                        <Tooltip title="Download">
                          <Button
                            size="small"
                            onClick={props.onClick}
                            icon={<FontAwesomeIcon icon="fa-solid fa-download" />}
                          />
                        </Tooltip>
                      )}
                    </Download>
                  </div>
                </div>
              </Space>
            );
          }}
        </Toolbar>
      ),
    },
  });

  const themeContext = { currentTheme, setCurrentTheme };

  const handleLoadError = (error: any) => {
    console.error('PDF loading error:', error);
    return (
      <FileErrorState
        error="Failed to load PDF file. The file may be corrupted or unsupported."
        fileName={fileUrl.split('/').pop()}
      />
    );
  };

  const handleLoadProgress = (percentages: any) => {
    return <FileLoadingState percentage={percentages} />;
  };

  return (
    <ThemeContext.Provider value={themeContext}>
      <div style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}>
        <Viewer
          fileUrl={fileUrl}
          plugins={[
            defaultLayoutPluginInstance,
            highlightPluginInstance,
            themePluginInstance,
            getFilePluginInstance,
          ]}
          theme={currentTheme}
          renderError={handleLoadError}
          renderLoader={handleLoadProgress}
          defaultScale={SpecialZoomLevel.PageWidth}
          initialPage={0}
        />
      </div>
    </ThemeContext.Provider>
  );
};
